import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  useSnackbar
} from '@growdash/design-system/atoms';
// components
// hooks
// store
import { RootState } from '@store/index';
// api
import { DataTable, MultiSelect } from '@growdash/design-system/molecules';
import { ManagementService } from '@modules/management/api';
import { Loader2 } from 'lucide-react';

export default function FoodCostTab() {
  const { enqueueSnackbar } = useSnackbar();

  const { BRANDS } = useSelector((state: RootState) => state.filter);
  const [selectedBrandIds, setSelectedBrandIds] = useState<
    number[] | undefined
  >(undefined);
  const [tableData, setTableData] = useState<any | null>(null);
  const [editObject, setEditObject] = useState<any>(null);
  const [selectedCost, setSelectedCost] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableLoading, setTableLoading] = useState(true);

  const [editCostLoading, setEditCostLoading] = useState(false);

  const tableLabels = [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Brand',
      align: 'center'
    },
    {
      id: 'food_cost',
      accessorKey: 'food_cost',
      header: '% Food Cost (From Gross)',
      cell: ({ row }: any) => {
        return <Badge variant='secondary'>{row.original.food_cost}%</Badge>;
      }
    },
    {
      id: 'actions',
      header: 'Action',
      align: 'center',
      cell: ({ row }: any) => {
        const isEditPending =
          editCostLoading && row.original.id === editObject?.id;
        return (
          <div className='flex gap-2'>
            {!editCostLoading && editObject?.id === row.original.id ? (
              <>
                <Input
                  autoFocus={true}
                  type='number'
                  value={selectedCost}
                  onChange={e => {
                    setSelectedCost(e.target.value);
                  }}
                  className='w-24'
                />
                <Button
                  size='sm'
                  disabled={editCostLoading}
                  onClick={() => {
                    editSave(row.original);
                  }}
                >
                  Save
                </Button>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => {
                    setEditObject(null);
                    setSelectedCost('');
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                disabled={editCostLoading}
                variant='secondary'
                size='sm'
                onClick={() => handleEdit(row.original)}
                className='text-sm'
              >
                {isEditPending && <Loader2 className='animate-spin' />}
                {isEditPending ? 'Editing' : 'Edit'}
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  const fetchFoodCost = useCallback(
    async (page: number, take: number) => {
      try {
        const commissionResponse = await ManagementService?.getAllFoodCost(
          page,
          take,
          selectedBrandIds
        );
        setTableLoading(true);
        setTableData(commissionResponse);
      } catch (error) {
        setTableLoading(false);
        enqueueSnackbar(error?.message, { variant: 'error' });
      } finally {
        setTableLoading(false);
      }
    },
    [selectedBrandIds, enqueueSnackbar]
  );

  const handleEdit = useCallback((row: any) => {
    setEditObject(row);
    setSelectedCost(row.food_cost?.toString() ?? '');
  }, []);

  // const handleCostChange = useCallback((value: string) => {
  //   setSelectedCost(value);
  // }, []);

  const editSave = async (row: any) => {
    try {
      if (!row) {
        enqueueSnackbar('No item selected for edit', { variant: 'error' });
        return;
      }

      if (!selectedCost || isNaN(Number(selectedCost))) {
        enqueueSnackbar('Please enter a valid food cost', { variant: 'error' });
        return;
      }
      setEditCostLoading(true);
      await ManagementService.UpdateFoodCost(
        {
          food_cost: Number(selectedCost)
        },
        row.id
      );

      enqueueSnackbar('Successfully updated food cost!', {
        variant: 'success'
      });
      setEditObject(null);
      fetchFoodCost(page + 1, rowsPerPage);
      setSelectedCost('');
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to update food cost', {
        variant: 'error'
      });
    } finally {
      setEditCostLoading(false);
    }
  };

  useEffect(() => {
    fetchFoodCost(page + 1, rowsPerPage);
  }, [selectedBrandIds, page, rowsPerPage, fetchFoodCost]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchFoodCost(newPage + 1, rowsPerPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchFoodCost(page + 1, newRowsPerPage);
  };

  return (
    <>
      <div className='mt-6'>
        <Card>
          <CardHeader>
            <div className='flex justify-end items-center'>
              <MultiSelect
                disabled={tableLoading}
                placeholder='Select Brands'
                popoverAlign='end'
                onReset={() => setSelectedBrandIds([])}
                className='w-max'
                maxCount={2}
                defaultValue={selectedBrandIds ?? ([] as any)}
                options={BRANDS?.map((brand: any) => ({
                  label: brand.name,
                  value: brand.id
                }))}
                onValueChange={setSelectedBrandIds}
              />
            </div>
          </CardHeader>
          <CardContent>
            <DataTable
              isLoading={tableLoading}
              isPaginationActive
              columns={tableLabels}
              data={tableData?.data || []}
              isServerSide
              pagination={{
                pageSize: rowsPerPage,
                pageIndex: page,
                pageCount: Math.ceil((tableData?.total ?? 0) / rowsPerPage),
                onPageChange: handlePageChange,
                onRowsPerPageChange: handleRowsPerPageChange
              }}
            />
          </CardContent>
        </Card>
      </div>
    </>
  );
}
